import React, { useCallback, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import { SeriesClickEventObject } from 'highcharts';
import { isEmpty, isNil } from 'lodash';
import { Typography } from '@material-ui/core';
import { generateLineGraphConfig } from 'src/components/Visualize/Visualize.utils';
import {
  visualizeButtonContainer,
  graphGroupByContainer,
  getVisualizeChartItem,
  metricLineGraphContainer,
  lineGraphContainer,
} from 'src/components/Visualize/Visualize.styles';
import { TenantConfigViewData, TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { Overlay } from 'src/common-ui';
import Highcharts from 'highcharts';
import { ConfigurableGridGraph } from 'configuration-schemas/viewdefns/general';
import SubheaderDropdown from 'src/components/Subheader/SubheaderDropdown';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { ViewDataState } from 'src/types/Domain';
import { ConfigurableGridGroupBySelection } from 'src/components/ConfigurableGrid/ConfigurableGrid.slice';

export const viewDefnToButton = (
  clickHandler: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void,
  currentSelectedIndex: number,
  viewItem: TenantConfigViewItem,
  index: number,
): JSX.Element => {
  return (
    <span
      className={getVisualizeChartItem(index === currentSelectedIndex)}
      key={index}
      id={viewItem.text}
      data-index={index}
      onClick={clickHandler}
    >
      {viewItem.text}
    </span>
  );
};

interface MetricLineGraphProps {
  selectionRequired: boolean;
  height: number;
  timeDataIndex?: ConfigurableGridGraph['timeDataIndex'];
  /** Graph config, data, categories and load state, all sourced from redux by the parent. */
  viewDefn: TenantConfigViewData | undefined;
  // FIXME: needs type fixed instead of BasicPivotItem[][][]
  data: BasicPivotItem[][][] | undefined;
  categories: string[] | undefined;
  graphState: ViewDataState;
  /** Selected graph group-by option index (lifted to redux for data fetching). */
  groupBySelection?: ConfigurableGridGroupBySelection | undefined;
  /** Change the group-by selection (parent refetches via redux). */
  onGroupByChange: (index: number) => void;
  onItemClick?: (data: unknown, dataIndex?: string) => void;
  setLineGraphConfig?: (config: TenantConfigViewItem) => void;
  onMouseOver: (e: React.MouseEvent) => void;
  onMouseOut: () => void;
}

const MetricLineGraph = ({
  selectionRequired,
  height,
  timeDataIndex = 'week',
  viewDefn,
  data,
  categories,
  graphState,
  groupBySelection,
  onGroupByChange,
  onItemClick,
  setLineGraphConfig,
  onMouseOver,
  onMouseOut,
}: MetricLineGraphProps) => {
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);

  const groupBy = typeof timeDataIndex === 'object' ? timeDataIndex : undefined;
  const selectedGroupByIndex = groupBySelection?.selectedIndex ?? groupBy?.defaultSelection ?? 0;
  const selectedGroupBy = groupBy?.options[selectedGroupByIndex] ?? groupBy?.options[0];
  const selectedDataIndex = selectedGroupBy?.dataIndex;

  const onLineGraphItemClick = useCallback(
    (event: SeriesClickEventObject) => {
      if (isNil(onItemClick)) {
        return;
      }
      const item = event.point.options;
      onItemClick(item, selectedDataIndex);
    },
    [onItemClick, selectedDataIndex],
  );

  const onLineGraphButtonClick = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (event.currentTarget.dataset.index) {
      setSelectedButtonIndex(parseFloat(event.currentTarget.dataset.index));
    }
  }, []);

  const viewDefnToButtonApplied = viewDefnToButton.bind(null, onLineGraphButtonClick, selectedButtonIndex);
  const loading = graphState !== ViewDataState.liveDataReady || isNil(viewDefn) || isNil(data) || isNil(categories);

  if (loading) {
    return (
      <section style={{ minHeight: `${height}px` }} className={metricLineGraphContainer}>
        <Overlay type="loading" visible={true} fitParent={true} />
      </section>
    );
  }

  const lineGraphConfig = generateLineGraphConfig(
    categories,
    data as BasicPivotItem[][][],
    viewDefn,
    selectedButtonIndex,
    height,
    onMouseOver,
    onMouseOut,
    onLineGraphItemClick,
    setLineGraphConfig,
  );

  return (
    <section className={metricLineGraphContainer}>
      <div className={visualizeButtonContainer}>
        {groupBy && (
          <div className={graphGroupByContainer}>
            <SubheaderDropdown
              label={groupBy.label}
              options={groupBy.options as TenantConfigViewItem[]}
              selection={groupBy.options[selectedGroupByIndex] ? selectedGroupByIndex : 0}
              handleChangeOnDropdown={(event) => onGroupByChange(Number(event.target.value))}
              dataQa="graph-group-by"
            />
          </div>
        )}
        {viewDefn.graphs?.primary.map(viewDefnToButtonApplied)}
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={lineGraphConfig}
        containerProps={{ className: lineGraphContainer }}
        immutable={true}
      />
    </section>
  );
};
export default MetricLineGraph;
